'use client'
import { type FC } from 'react'
import { VStack } from '@chakra-ui/react'
import type { PickupDTO } from 'ecosystem'
import { getDocumentLanguage, resolveCountryName } from 'shared-utils'
import { CheckoutSectionHeading } from '../../CheckoutSectionHeading'
import { GrandRadioCard } from 'ui'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'

// Override keys keep the legacy `invoice*` prefix so the existing
// `invoicePaymentTranslations` bundle resolves them across all flows.
export interface DefaultPickupSelectorOverrides {
  invoicePickupHeading?: string
}

interface DefaultPickupSelectorProps {
  pickups: PickupDTO[]
  selectedId: string | undefined
  onChange: (next: PickupDTO) => void
}

/**
 * Pickup-location picker for the DEFAULT shipping branch. Pickup options
 * come from the store config (forwarded via `useCheckoutContext`).
 * Mirrors `PostNordPickupSelector` visually so the two flows look the
 * same to the shopper.
 */
export const DefaultPickupSelector: FC<DefaultPickupSelectorProps> = ({
  pickups,
  selectedId,
  onChange
}) => {
  const overrides = useOverridesContext<keyof DefaultPickupSelectorOverrides>()
  const locale = getDocumentLanguage()

  const heading = overrideText('Välj upphämtningsställe', overrides?.invoicePickupHeading)

  const handleChange = (id: string) => {
    const next = pickups.find((p) => p.id === id)
    if (next) onChange(next)
  }

  return (
    <VStack align="stretch" gap={3}>
      <CheckoutSectionHeading>{heading}</CheckoutSectionHeading>

      <GrandRadioCard.Root
        onChange={handleChange}
        size="md"
        value={selectedId ?? ''}
        variant="outline"
      >
        <VStack align="stretch" gap={3}>
          {pickups.map((pickup) => {
            const country =
              pickup.address.country &&
              (resolveCountryName(pickup.address.country, locale) ?? pickup.address.country)
            const lines = [
              pickup.address.address1,
              pickup.address.address2,
              [pickup.address.postalCode, pickup.address.postalRegion].filter(Boolean).join(' '),
              country,
              pickup.address.mobilePhone,
              pickup.address.attention,
              pickup.address.reference
            ].filter(Boolean) as string[]

            return (
              <GrandRadioCard.Item key={pickup.id} value={pickup.id}>
                <GrandRadioCard.ItemHeader>
                  <GrandRadioCard.ItemContent>
                    <GrandRadioCard.ItemText>{pickup.name}</GrandRadioCard.ItemText>
                    {lines.map((line) => (
                      <GrandRadioCard.ItemDescription key={line}>
                        {line}
                      </GrandRadioCard.ItemDescription>
                    ))}
                  </GrandRadioCard.ItemContent>
                </GrandRadioCard.ItemHeader>
              </GrandRadioCard.Item>
            )
          })}
        </VStack>
      </GrandRadioCard.Root>
    </VStack>
  )
}
